import {
    clamp,
    EditorArea,
    MIN_BBOX_METERS,
    Point,
    SizeMeters,
    snapMeters,
    snapPointMeters,
    unitPolygonToMetres,
} from './mapGeometry';

// Mirrors TablesMapManager.ValidateAreaShape (MinAreaPolygonPoints / MaxAreaPolygonPoints).
export const OUTLINE_MIN_CORNERS = 3;
export const OUTLINE_MAX_CORNERS = 64;
export const GRID_SNAP_METERS = 0.5;

const ANGLE_SNAP_RADIANS = Math.PI / 4;
const FREEHAND_START_TOLERANCE_METERS = 0.05;
const EPSILON = 1e-9;

export type OutlineError =
    | 'tooFewCorners'
    | 'tooManyCorners'
    | 'notClosed'
    | 'tooSmall'
    | 'outOfBounds'
    | 'selfIntersecting';

export interface SnapOptions {
    grid: boolean;
    angle: boolean;
}

function roundTo(value: number, step: number): number {
    return Math.round(value / step) * step;
}

/** A wall off the grid (a 5.14 m map) must stay reachable: it wins when the pointer is nearer to it than to the snapped value. */
function preferWall(raw: number, snapped: number, max: number): number {
    if (Math.abs(raw) < Math.abs(raw - snapped)) return 0;
    if (Math.abs(raw - max) < Math.abs(raw - snapped)) return max;
    return snapped;
}

export function areaToCorners(area: EditorArea): Point[] {
    if (area.kind === 'Ellipse') return [];
    if (area.kind === 'Polygon') return unitPolygonToMetres(area).map(snapPointMeters);
    const { offset, size } = area;
    return [
        { x: offset.x, y: offset.y },
        { x: offset.x + size.width, y: offset.y },
        { x: offset.x + size.width, y: offset.y + size.height },
        { x: offset.x, y: offset.y + size.height },
    ].map(snapPointMeters);
}

export function cornersToArea(corners: Point[]): EditorArea {
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    const offset = snapPointMeters({ x: Math.min(...xs), y: Math.min(...ys) });
    const size = {
        width: snapMeters(Math.max(...xs) - offset.x),
        height: snapMeters(Math.max(...ys) - offset.y),
    };
    const points = corners.map((p) => ({
        x: size.width > 0 ? clamp((p.x - offset.x) / size.width, 0, 1) : 0,
        y: size.height > 0 ? clamp((p.y - offset.y) / size.height, 0, 1) : 0,
    }));
    return { kind: 'Polygon', offset, size, points };
}

export function snapCorner(
    raw: Point,
    prev: Point | null,
    options: SnapOptions,
    mapSize: SizeMeters
): Point {
    let p = raw;
    if (options.angle && prev) {
        const dx = raw.x - prev.x;
        const dy = raw.y - prev.y;
        const theta = Math.round(Math.atan2(dy, dx) / ANGLE_SNAP_RADIANS) * ANGLE_SNAP_RADIANS;
        const ux = Math.round(Math.cos(theta));
        const uy = Math.round(Math.sin(theta));
        const isDiagonal = ux !== 0 && uy !== 0;
        const along = dx * ux + dy * uy;
        const step = isDiagonal ? along / 2 : along;
        const snappedStep = options.grid ? roundTo(step, GRID_SNAP_METERS) : step;
        p = { x: prev.x + ux * snappedStep, y: prev.y + uy * snappedStep };
        // Only axis-aligned segments may take the wall; a diagonal would bend off 45°.
        if (options.grid && !isDiagonal) {
            p =
                ux !== 0
                    ? { ...p, x: preferWall(raw.x, p.x, mapSize.width) }
                    : { ...p, y: preferWall(raw.y, p.y, mapSize.height) };
        }
    } else if (options.grid) {
        p = {
            x: preferWall(raw.x, roundTo(raw.x, GRID_SNAP_METERS), mapSize.width),
            y: preferWall(raw.y, roundTo(raw.y, GRID_SNAP_METERS), mapSize.height),
        };
    }
    return snapPointMeters({
        x: clamp(p.x, 0, mapSize.width),
        y: clamp(p.y, 0, mapSize.height),
    });
}

export function rectangleCorners(a: Point, b: Point): Point[] {
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    return [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY },
    ];
}

export function segmentMeasure(a: Point, b: Point): { length: number; angleToHorizontal: number } {
    const dx = Math.abs(b.x - a.x);
    const dy = Math.abs(b.y - a.y);
    return { length: Math.hypot(dx, dy), angleToHorizontal: (Math.atan2(dy, dx) * 180) / Math.PI };
}

export function outlineEdges(
    corners: Point[],
    closed: boolean
): { a: Point; b: Point; length: number; normal: Point }[] {
    const n = corners.length;
    let twiceArea = 0;
    for (let i = 0; i < n; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % n];
        twiceArea += a.x * b.y - b.x * a.y;
    }
    // Y grows downwards, so a positive shoelace sum runs clockwise on screen.
    const side = twiceArea >= 0 ? 1 : -1;
    const edgeCount = closed ? n : Math.max(n - 1, 0);
    return Array.from({ length: edgeCount }, (_, i) => {
        const a = corners[i];
        const b = corners[(i + 1) % n];
        const length = Math.hypot(b.x - a.x, b.y - a.y);
        const normal =
            length === 0
                ? { x: 0, y: 0 }
                : { x: (side * (b.y - a.y)) / length, y: (-side * (b.x - a.x)) / length };
        return { a, b, length, normal };
    });
}

function orientation(a: Point, b: Point, c: Point): number {
    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    if (Math.abs(cross) < EPSILON) return 0;
    return cross > 0 ? 1 : -1;
}

/** `p` is collinear with a–b; true when it lies within the segment's extent. */
function isWithinSegment(p: Point, a: Point, b: Point): boolean {
    return (
        p.x >= Math.min(a.x, b.x) - EPSILON &&
        p.x <= Math.max(a.x, b.x) + EPSILON &&
        p.y >= Math.min(a.y, b.y) - EPSILON &&
        p.y <= Math.max(a.y, b.y) + EPSILON
    );
}

function segmentsTouch(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
    const o1 = orientation(a1, a2, b1);
    const o2 = orientation(a1, a2, b2);
    const o3 = orientation(b1, b2, a1);
    const o4 = orientation(b1, b2, a2);
    if (o1 !== o2 && o3 !== o4) return true;
    return (
        (o1 === 0 && isWithinSegment(b1, a1, a2)) ||
        (o2 === 0 && isWithinSegment(b2, a1, a2)) ||
        (o3 === 0 && isWithinSegment(a1, b1, b2)) ||
        (o4 === 0 && isWithinSegment(a2, b1, b2))
    );
}

/** Two edges sharing `shared` overlap only when they fold back onto each other. */
function adjacentEdgesOverlap(shared: Point, a: Point, b: Point): boolean {
    if (orientation(shared, a, b) !== 0) return false;
    return isWithinSegment(b, shared, a) || isWithinSegment(a, shared, b);
}

function isSelfIntersecting(corners: Point[]): boolean {
    const n = corners.length;
    for (let i = 0; i < n; i++) {
        const a1 = corners[i];
        const a2 = corners[(i + 1) % n];
        for (let j = i + 1; j < n; j++) {
            const b1 = corners[j];
            const b2 = corners[(j + 1) % n];
            if (j === i + 1) {
                if (adjacentEdgesOverlap(a2, a1, b2)) return true;
            } else if (i === 0 && j === n - 1) {
                if (adjacentEdgesOverlap(a1, a2, b1)) return true;
            } else if (segmentsTouch(a1, a2, b1, b2)) {
                return true;
            }
        }
    }
    return false;
}

/** Every condition the backend's ValidateAreaShape applies to a Polygon, plus closed + simple outline. */
export function validateOutline(
    corners: Point[],
    closed: boolean,
    mapSize: SizeMeters
): OutlineError | null {
    if (corners.length < OUTLINE_MIN_CORNERS) return 'tooFewCorners';
    if (corners.length > OUTLINE_MAX_CORNERS) return 'tooManyCorners';
    if (!closed) return 'notClosed';
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    if (maxX - minX < MIN_BBOX_METERS || maxY - minY < MIN_BBOX_METERS) return 'tooSmall';
    if (minX < 0 || minY < 0 || maxX > mapSize.width || maxY > mapSize.height) {
        return 'outOfBounds';
    }
    if (isSelfIntersecting(corners)) return 'selfIntersecting';
    return null;
}

export function outlineStats(
    corners: Point[],
    closed: boolean
): { perimeter: number; area: number } {
    let perimeter = 0;
    let twiceArea = 0;
    const edgeCount = closed ? corners.length : corners.length - 1;
    for (let i = 0; i < edgeCount; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % corners.length];
        perimeter += Math.hypot(b.x - a.x, b.y - a.y);
        twiceArea += a.x * b.y - b.x * a.y;
    }
    return { perimeter, area: closed ? Math.abs(twiceArea) / 2 : 0 };
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;
    const t = lenSq === 0 ? 0 : clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq, 0, 1);
    return Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

function douglasPeucker(points: Point[], tolerance: number): Point[] {
    if (points.length < 3) return points;
    const first = points[0];
    const last = points[points.length - 1];
    let farthestIndex = 0;
    let farthestDistance = 0;
    for (let i = 1; i < points.length - 1; i++) {
        const d = distanceToSegment(points[i], first, last);
        if (d > farthestDistance) {
            farthestDistance = d;
            farthestIndex = i;
        }
    }
    if (farthestDistance <= tolerance) return [first, last];
    const left = douglasPeucker(points.slice(0, farthestIndex + 1), tolerance);
    const right = douglasPeucker(points.slice(farthestIndex), tolerance);
    return [...left.slice(0, -1), ...right];
}

export function freehandToCorners(
    stroke: Point[],
    snapToGrid: boolean,
    mapSize: SizeMeters
): Point[] {
    let tolerance = FREEHAND_START_TOLERANCE_METERS;
    let simplified = douglasPeucker(stroke, tolerance);
    while (simplified.length > OUTLINE_MAX_CORNERS) {
        tolerance *= 1.5;
        simplified = douglasPeucker(stroke, tolerance);
    }
    const snapped = simplified.map((p) =>
        snapCorner(p, null, { grid: snapToGrid, angle: false }, mapSize)
    );
    const corners = snapped.filter(
        (p, i) => i === 0 || p.x !== snapped[i - 1].x || p.y !== snapped[i - 1].y
    );
    const closesOnItself =
        corners.length > 1 &&
        Math.hypot(
            corners[0].x - corners[corners.length - 1].x,
            corners[0].y - corners[corners.length - 1].y
        ) <= tolerance;
    return closesOnItself ? corners.slice(0, -1) : corners;
}
